// Provider-subagent domain — pure types + selectors (DOM-free, unit-testable).
//
// A "provider subagent" is a child agent SPAWNED BY THE PROVIDER (Codex /
// OpenCode / Claude), not by Paseo's agent-to-agent orchestration. Paseo's own
// sub-agents carry a `parentAgentId` and show up as full Agent rows in the
// workspace list (SubagentsPanel already renders those). Provider subagents live
// only in the daemon's ProviderSubagentStore and reach the UI as a stream of
// `provider_subagent` update events — they are NOT in `workspaceState.agents`.
//
// This module reimplements the concepts from Paseo's
// packages/app/src/subagents/{provider-store,select,track-presentation}.ts
// (React/zustand → framework-free), so the reactive store + the Svelte track can
// share one tested core. Keep it side-effect free: no `$state`, no imports of the
// app-state module — only types and pure functions.

import { formatTokens } from "./context-window.js";
import type { AgentTimelineItem } from "./types.js";

export type ProviderSubagentStatus = "running" | "completed" | "failed" | "canceled";

// Mirror of the daemon's ProviderSubagentDescriptor (server
// provider-subagents/store.ts) and the wire schema
// (ProviderSubagentDescriptorPayloadSchema). Additive fields are optional so an
// older daemon that omits them still parses.
export interface ProviderSubagentDescriptor {
  id: string;
  parentAgentId: string;
  provider: string;
  title: string | null;
  description: string | null;
  status: ProviderSubagentStatus;
  createdAt: string;
  updatedAt: string;
  toolCallId?: string | null;
  cwd?: string | null;
  // Cumulative tokens the subagent has spent (running total, frozen when it finishes).
  // Absent from an older daemon.
  tokens?: number | null;
  // The provider subagent that launched this one (#4321 nesting, W5). Absent/null = a direct
  // child of the agent. Drives the nested tree in the panel.
  parentSubagentId?: string | null;
}

// The wire update the daemon pushes as it observes provider subagents. Mirrors
// the protocol `agent.provider_subagents.update` payload (discriminated on
// `kind`): a descriptor upsert, a single timeline row, or a removal.
export type ProviderSubagentUpdate =
  | { kind: "upsert"; subagent: ProviderSubagentDescriptor }
  | {
      kind: "timeline";
      parentAgentId: string;
      subagentId: string;
      provider: string;
      item: AgentTimelineItem;
      timestamp: string;
      seq: number;
      epoch: string;
    }
  | { kind: "remove"; parentAgentId: string; subagentId: string };

// A flattened, render-ready row for the subagents track. `kind: "provider"`
// distinguishes it from the Paseo Agent rows the panel also renders.
export interface ProviderSubagentRow {
  kind: "provider";
  id: string;
  parentAgentId: string;
  provider: string;
  label: string;
  // The one-line task the child was spawned with, when it is not already the label. `label`
  // alone is the agent TYPE ("implementer"), which is identical across a fan-out — this is
  // what tells two implementers apart.
  detail: string | null;
  status: ProviderSubagentStatus;
  createdAt: number;
  requiresAttention: boolean;
  // The COST of the row, on the row (see subagentRowMetrics). `finishedAt` is null while
  // the subagent runs — that null IS the "keep ticking" signal — and the descriptor's last
  // update once it has stopped, which is what freezes the duration on its final value.
  finishedAt: number | null;
  tokens: number | null;
  // The provider subagent that launched this one, or null for a direct child (W5). Drives nesting.
  parentSubagentId: string | null;
}

// Stable map key for a (parent, subagent) pair. The store keys descriptors on
// this so a subagent id that repeats under different parents never collides.
export function providerSubagentKey(parentAgentId: string, subagentId: string): string {
  return `${parentAgentId}::${subagentId}`;
}

// Map a provider-subagent status onto the Agent lifecycle vocabulary the shared
// attention/badge helpers already speak ("running" | "idle" | "error").
export function providerSubagentLifecycle(status: ProviderSubagentStatus): string {
  if (status === "running") return "running";
  if (status === "failed") return "error";
  return "idle";
}

// A subagent has "finished" once it's no longer running (completed / failed /
// canceled) — the archive-finished affordance targets exactly these.
export function isProviderSubagentFinished(status: ProviderSubagentStatus): boolean {
  return status !== "running";
}

// Resolve a human label from the descriptor's description (what the subagent was asked), else its title/type. Mirrors Paseo's
// resolveRowLabel: a blank or placeholder ("new agent") title is treated as "not
// yet named" so the row can show a loading affordance instead of a dummy name.
export function resolveProviderSubagentLabel(
  title: string | null | undefined,
  description?: string | null,
): string | null {
  for (const candidate of [description, title]) {
    if (typeof candidate !== "string") continue;
    const normalized = candidate.trim();
    if (!normalized) continue;
    if (normalized.toLowerCase() === "new agent") continue;
    return normalized;
  }
  return null;
}

// The row label with a stable fallback for a not-yet-named subagent.
export function providerSubagentRowLabel(
  desc: Pick<ProviderSubagentDescriptor, "title" | "description">,
): string {
  return resolveProviderSubagentLabel(desc.title, desc.description) ?? "Subagent";
}

// The row's SECOND line-item: the title/type (the description is the label, CYBORG-1312). Null when it is
// missing or already serving as the label, so the same words are never printed twice on one row.
export function providerSubagentRowDetail(desc: ProviderSubagentDescriptor): string | null {
  const detail = resolveProviderSubagentLabel(desc.title);
  return detail !== null && detail !== providerSubagentRowLabel(desc) ? detail : null;
}

// Build a render row from a descriptor.
export function toProviderSubagentRow(desc: ProviderSubagentDescriptor): ProviderSubagentRow {
  return {
    kind: "provider",
    id: desc.id,
    parentAgentId: desc.parentAgentId,
    provider: desc.provider,
    label: providerSubagentRowLabel(desc),
    detail: providerSubagentRowDetail(desc),
    status: desc.status,
    createdAt: Date.parse(desc.createdAt) || 0,
    requiresAttention: desc.status === "failed",
    finishedAt: desc.status === "running" ? null : Date.parse(desc.updatedAt) || null,
    tokens: typeof desc.tokens === "number" && Number.isFinite(desc.tokens) ? desc.tokens : null,
    parentSubagentId: typeof desc.parentSubagentId === "string" ? desc.parentSubagentId : null,
  };
}

// A provider-subagent row plus its depth in the nested tree (#4321 ownership, W5). Depth 0 is a
// direct child of the agent; a child row sits one deeper than the subagent that launched it.
export interface NestedProviderSubagentRow {
  row: ProviderSubagentRow;
  depth: number;
}

// Order flat rows into the nested tree the panel renders: each parent immediately followed by its
// descendants (pre-order), siblings kept in their incoming order. A row whose parentSubagentId is
// null — or points at a subagent not in this set (a pruned/foreign parent) — is a root, so nothing
// is ever dropped. Cycle-safe: a row is emitted at most once, so a malformed parent chain degrades
// to a flat list instead of looping.
export function nestProviderSubagentRows(
  rows: readonly ProviderSubagentRow[],
): NestedProviderSubagentRow[] {
  const present = new Set(rows.map((r) => r.id));
  const childrenByParent = new Map<string, ProviderSubagentRow[]>();
  const roots: ProviderSubagentRow[] = [];
  for (const row of rows) {
    const parent = row.parentSubagentId;
    if (parent && parent !== row.id && present.has(parent)) {
      const siblings = childrenByParent.get(parent) ?? [];
      siblings.push(row);
      childrenByParent.set(parent, siblings);
    } else {
      roots.push(row);
    }
  }
  const out: NestedProviderSubagentRow[] = [];
  const emitted = new Set<string>();
  const walk = (row: ProviderSubagentRow, depth: number): void => {
    if (emitted.has(row.id)) return;
    emitted.add(row.id);
    out.push({ row, depth });
    for (const child of childrenByParent.get(row.id) ?? []) walk(child, depth + 1);
  };
  for (const root of roots) walk(root, 0);
  // A row stranded by a parent cycle (never reached from a root) still shows, at depth 0.
  for (const row of rows) if (!emitted.has(row.id)) walk(row, 0);
  return out;
}

// ── What a subagent COST, on its own row ──────────────────────────────────────────────
//
// The panel used to render four identical anonymous rows: no way to tell which child was
// which, how long it had taken, or what it had spent. The Claude Code terminal puts both
// numbers right on the line it prints for a finished Task, and that is the shape copied
// here — one line, identity first, then time, then tokens.
//
// THE ONE PLACE A CLOCK IS ALLOWED. The work-session header's elapsed timer was cut after
// review and a contract test keeps it out (session-live-presentation.source.test.ts). This
// is not that: it is a per-child cost readout inside the fan-out list, and it lives here so
// both surfaces that render the list read the SAME derivation.

export interface SubagentRowMetrics {
  /** "1m 14s" — ticking while it runs, frozen on the final duration once it finishes. */
  elapsedLabel: string | null;
  /** "394k" — null when the provider reported no usage at all. */
  tokensLabel: string | null;
  /** Whether the elapsed value is still moving, so the caller knows to keep a tick running. */
  running: boolean;
}

// The row's own slice of the descriptor. Both list surfaces pass their own row type in.
export interface SubagentRowTiming {
  startedAt: number | null;
  finishedAt: number | null;
  tokens: number | null;
}

/**
 * Elapsed + tokens for ONE row, at instant `now`.
 *
 * A running row measures against `now`, so re-evaluating it with a fresh `now` is what makes
 * the clock tick. A finished one measures against `finishedAt` and therefore returns the same
 * string forever, no matter how often it is re-evaluated — the freeze is structural, not a
 * flag someone has to remember to set.
 */
export function subagentRowMetrics(row: SubagentRowTiming, now: number): SubagentRowMetrics {
  const running = row.finishedAt === null;
  const startedAt = row.startedAt !== null && row.startedAt > 0 ? row.startedAt : null;
  const until = running ? now : row.finishedAt;
  const elapsedMs = startedAt !== null && until !== null ? until - startedAt : null;
  return {
    // A negative span means the two clocks disagree (the daemon stamps createdAt, the
    // browser supplies `now`), and a backwards duration is worse than none.
    elapsedLabel: elapsedMs !== null && elapsedMs >= 0 ? formatSubagentElapsed(elapsedMs) : null,
    // Same compact form the context-window meter already speaks ("84k", "1.5M") — one token
    // vocabulary across the app, not a second one invented for this row.
    tokensLabel:
      row.tokens !== null && row.tokens > 0 ? formatTokens(Math.round(row.tokens)) : null,
    running,
  };
}

/** "8s", "1m 14s", "2h 5m" — coarse on purpose, so a long row does not read as a stopwatch. */
export function formatSubagentElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    const seconds = totalSeconds % 60;
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes > 0 ? `${hours}h ${restMinutes}m` : `${hours}h`;
}

// A completed child folds out of the track's VIEW so the panel reads as what is still
// running. A FILTER: nothing is archived here (see SUBAGENT_AUTO_ARCHIVE_MS below for that).

/** How long a completed child stays listed after it finishes, before folding away. */
export const SUBAGENT_SETTLE_MS = 8_000;

/**
 * The rows the track renders at instant `now` — a filtered view; the input is never
 * mutated and no store is touched. A fresh `now` is what expires the settle window.
 */
export function visibleSubagentRows(
  rows: readonly ProviderSubagentRow[],
  now: number,
): ProviderSubagentRow[] {
  // Nothing running → nothing folds, so a finished fan-out is all there for review.
  if (!rows.some((r) => r.status === "running")) return [...rows];
  return rows.filter((r) => {
    // Only a clean completion folds: a failed or canceled child is the one worth reading.
    if (r.status !== "completed") return true;
    // An unparseable finish time has no settle clock — keep it rather than guess.
    if (r.finishedAt === null) return true;
    // A beat before it goes, so it does not vanish from under someone reading it.
    return now - r.finishedAt < SUBAGENT_SETTLE_MS;
  });
}

/**
 * How long a completed or canceled child stays on the track before it archives itself (native
 * Claude Code parity): long enough to see it finish, short enough that a fan-out does not pile
 * up. A failed child never auto-archives, so a failure is noticed; it stays until dismissed.
 */
export const SUBAGENT_AUTO_ARCHIVE_MS = 20_000;

/**
 * One-shot timer for the soonest auto-archive among `rows`; `archive` gets every row due by
 * then. Re-arm from a $effect; returns the cancel. `heardAt` is this client's own clock for the
 * child (no daemon/browser skew); `keepId` is the row open in the inspector.
 */
export function scheduleSubagentAutoArchive(input: {
  rows: readonly ProviderSubagentRow[];
  heardAt: (subagentId: string) => number | null;
  keepId: string | null;
  archive: (rows: ProviderSubagentRow[]) => void;
}): () => void {
  const due: { row: ProviderSubagentRow; at: number }[] = [];
  for (const row of input.rows) {
    if (row.id === input.keepId || row.status === "running" || row.status === "failed") continue;
    const settledAt = input.heardAt(row.id) ?? row.finishedAt;
    if (settledAt !== null) due.push({ row, at: settledAt + SUBAGENT_AUTO_ARCHIVE_MS });
  }
  if (due.length === 0) return () => {};
  const earliest = Math.min(...due.map((d) => d.at));
  const timer = setTimeout(
    () => {
      // Never below `earliest`: a timer that fires a hair early must still archive its target.
      const cutoff = Math.max(Date.now(), earliest);
      input.archive(due.filter((d) => d.at <= cutoff).map((d) => d.row));
    },
    Math.max(0, earliest - Date.now()),
  );
  return () => clearTimeout(timer);
}

// The collapsible header label — "N subagents · M running" (Paseo parity).
export function formatSubagentHeaderLabel(total: number, running: number): string {
  const parts = [`${total} ${total === 1 ? "subagent" : "subagents"}`];
  if (running > 0) parts.push(`${running} running`);
  return parts.join(" · ");
}

// Oldest-first ordering, matching Paseo's track (createdAt ascending).
export function sortProviderSubagentRows(rows: ProviderSubagentRow[]): ProviderSubagentRow[] {
  return [...rows].sort((a, b) => a.createdAt - b.createdAt);
}

// One page of a subagent's transcript, as cyborg:fetch_provider_subagent_timeline answers it.
export interface SubagentTranscriptPage {
  provider: string | null;
  rows: { seq: number; item: unknown }[];
  hasOlder: boolean;
  // Absent on the relay's durable answer, which cannot page back.
  epoch?: string;
}

// Page back from the tail to the first row; stops (still `hasOlder`) when a page brings
// nothing older (an old daemon, the durable mirror) or after `maxPages`.
export async function fetchWholeSubagentTranscript(
  fetchPage: (before?: { epoch: string; seq: number }) => Promise<SubagentTranscriptPage>,
  maxPages = 50,
): Promise<SubagentTranscriptPage> {
  const tail = await fetchPage();
  const pages = [tail.rows];
  let oldest = tail.rows[0]?.seq;
  let hasOlder = tail.hasOlder;
  for (let page = 1; hasOlder && tail.epoch && oldest !== undefined && page < maxPages; page++) {
    const before: number = oldest;
    const older = await fetchPage({ epoch: tail.epoch, seq: before });
    const prior = older.rows.filter((r) => r.seq < before);
    if (prior.length === 0) break;
    pages.push(prior);
    oldest = prior[0].seq;
    hasOlder = older.hasOlder;
  }
  return { ...tail, rows: pages.toReversed().flat(), hasOlder };
}
